import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import COMPANY_INFO from '../data/companyInfo';
import SignatureLines from './SignatureLines';
import ClientBlock from './ClientBlock';
import printWhenReady from '../utils/printWhenReady';
import './InvoicePrintView.css';
import { formatDay } from '../utils/dates';
import { useFileName, documentName, DOC_TYPES } from '../utils/fileName';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);

const DISCLAIMER = `Esta cotización es válida únicamente hasta la fecha indicada y está sujeta a cambios sin previo aviso. Los precios
no incluyen costos adicionales de entrega salvo que se indique lo contrario. Esta cotización no constituye una factura ni un
compromiso de venta; para formalizar la compra debe ser convertida en factura por Caribbean Gas Company, SRL.`;

const PENDING_STATUSES = ['draft', 'sent'];

const QuotePrintView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    API.get(`/quotes/${id}`).then(res => setQuote(res.data)).catch(err => {
      console.error('Error loading quote:', err);
      setError('No se pudo cargar la cotización.');
    });
  }, [id]);

  useEffect(() => {
    if (quote) {
      let cancelPrint = () => {};
      const t = setTimeout(() => { cancelPrint = printWhenReady(); }, 350);
      return () => { clearTimeout(t); cancelPrint(); };
    }
  }, [quote]);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const { data } = await API.post('/invoices', { quoteId: id });
      window.open(`/invoices/${data._id}/print`, '_blank');
      navigate('/quotes');
    } catch (err) {
      console.error('Error converting quote to invoice:', err);
      setError(err.response?.data?.message || 'Error al convertir la cotización a factura.');
      setConverting(false);
    }
  };

  // Saved as PDF: <Cliente>Cotizacion (utils/fileName.js); a quote for someone not yet a client uses the name typed on it.
  useFileName(quote && documentName(quote.client?.name || quote.clientName, DOC_TYPES.cotizacion));

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!quote) return <div className="p-4 text-muted">Cargando cotización...</div>;

  const client = quote.client || {};

  return (
    <div className="invoice-page-wrapper">
      <div className="no-print text-center mb-3 d-flex justify-content-center gap-2">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="bi bi-printer" /> Imprimir / Guardar como PDF
        </button>
        {quote.status !== 'accepted' && (
          <button className="btn btn-outline-secondary" onClick={() => navigate(`/quotes/${id}/edit`)}>
            <i className="bi bi-pencil" /> Editar
          </button>
        )}
        {PENDING_STATUSES.includes(quote.status) && (
          <button className="btn btn-outline-primary" onClick={handleConvert} disabled={converting}>
            {converting ? 'Convirtiendo...' : 'Convertir a Factura'}
          </button>
        )}
      </div>

      <div className="invoice-paper">
        {/* .invoice-body flows normally; .invoice-bottom (signatures + thank-you note) is pinned to the bottom of the page in print. */}
        <div className="invoice-body">
        <div className="invoice-header-row">
          <div className="invoice-company-block">
            <img src="/CGCLogo.png" alt="Caribbean Gas Company" className="invoice-logo" />
            <div style={{ fontWeight: 700, color: '#0E1A24' }}>{COMPANY_INFO.name}</div>
            <div>RNC: {COMPANY_INFO.rnc}</div>
            <div>{COMPANY_INFO.address}</div>
            <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
            <div>{COMPANY_INFO.website}</div>
          </div>
          <div className="invoice-meta-block">
            <div className="invoice-title">COTIZACIÓN</div>
            <div>No.: {quote.number}</div>
            <div>Fecha: {dateStr(quote.createdAt)}</div>
            <div>Válida hasta: {dateStr(quote.validUntil)}</div>
          </div>
        </div>

        <ClientBlock label="Cotizado a" client={client} address={client.address} />

        <table className="invoice-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Descripción</th>
              <th className="text-center">Cant.</th>
              <th className="text-end">Precio Unit.</th>
              <th className="text-center">ITBIS</th>
              <th className="text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            {quote.items.map((item, i) => {
              const lineSubtotal = item.quantity * item.unitPrice;
              const lineItbis = item.itbisExento ? 0 : lineSubtotal * (item.itbisRate ?? 0.18);
              return (
                <tr key={i}>
                  <td>{item.sku || '—'}</td>
                  <td>{item.description}</td>
                  <td className="text-center">{item.quantity}</td>
                  <td className="text-end">{money(item.unitPrice)}</td>
                  <td className="text-center">{money(lineItbis)}</td>
                  <td className="text-end">{money(lineSubtotal + lineItbis)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="invoice-totals">
          <div className="invoice-totals-box">
            <div className="invoice-totals-row">
              <span>Subtotal</span>
              <span>{money(quote.subtotal)}</span>
            </div>
            <div className="invoice-totals-row">
              <span>ITBIS</span>
              <span>{money(quote.itbisTotal)}</span>
            </div>
            <div className="invoice-totals-row grand">
              <span>Total</span>
              <span>{money(quote.total)}</span>
            </div>
          </div>
        </div>

        {quote.notes && (
          <div className="invoice-bill-to">
            <div className="label">Notas</div>
            <div>{quote.notes}</div>
          </div>
        )}

        <div className="invoice-disclaimer">{DISCLAIMER}</div>

        </div>
        <div className="invoice-bottom">
        <SignatureLines clientName={client.name} />

        <div className="invoice-footer-note">Gracias por su preferencia — {COMPANY_INFO.name}</div>
        </div>
      </div>
    </div>
  );
};

export default QuotePrintView;
